// --- 🛰️ AUTHORITY CONVERGENCE TRACE 🛰️ ---
(function () {
  if (window.__BUILD_TRUTH__) {
    console.log('%c OS BUILD TRUTH ', 'background: #000; color: #00ff00; font-weight: bold; padding: 4px;', window.__BUILD_TRUTH__);
  }
})();
// --- END TRACE ---

import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/styles/global.css";
import App from "@/App";
import { UI_INVARIANTS } from "./config/ui-invariants";

// PHASE 2: UI RUNTIME INVARIANT GUARD (AG Execution Directive)
if (!UI_INVARIANTS || typeof UI_INVARIANTS !== 'object') {
  // Silent Failure Prevention: Halt immediately if invariants are missing
  console.error("FATAL: UI_INVARIANTS undefined at boot. Runtime Integrity Compromised.");
  document.body.innerHTML = '<div style="background:red;color:white;padding:20px;font-family:monospace"><h1>SYSTEM HALT</h1><p>UI_INVARIANTS_MISSING</p></div>';
  throw new Error("UI_INVARIANTS_MISSING: Module failed to export configuration.");
}
import { UIContractGuard } from "@/components/system/UIContractGuard";
import { renderCheckpoint } from "@/utils/renderCheckpoint";
import { TRUTH_MARKER } from "@/utils/truthMarker";
import { enforceRuntimeHydration } from "@/components/system/RuntimeHydrationGuard";
import { validateBootIdentity, renderSystemHalt } from "@/utils/bootValidation";
// Phase 23: Deterministic DOM Probe (Run automatically)
import "@/utils/domProbe";

// PKG-FRONT-BOOT-001: Pre-Mount Identity Validation
const bootValidation = validateBootIdentity();
if (!bootValidation.valid) {
  console.error(`%c [BOOT_GATE] ❌ INDEX HALT: ${bootValidation.code} `, 'background: #ff0000; color: #fff; font-weight: bold;');
  renderSystemHalt(bootValidation.code);
  throw new Error(`PKG-FRONT-BOOT-001_VIOLATION: ${bootValidation.code}`);
}

// APP_BOOT: First signal. No module context yet.
TRUTH_MARKER('APP_BOOT', 'SYSTEM', 'PASS');

console.log('App Entry Point Reached. Identity Verified.');

// PKG-013: Enforce Runtime State Reconciliation
enforceRuntimeHydration();

// Phase 3: Visual Law Enforcement (Dynamic CSS Variables)
UI_INVARIANTS.AUTHORITY.enforceVisualLaw();

// Phase 6: Transition to PRE_RENDER
UI_INVARIANTS.STATE.setPhase(UI_INVARIANTS.PHASES.PRE_RENDER);

const rootElement = document.getElementById("root");
if (!rootElement) {
  // CRITICAL: Integrity Breach Detected
  console.error("CLASS: FRONTEND_RUNTIME_INTEGRITY_VIOLATION");
  console.error("FATAL: Target container '#root' is missing from DOM.");
  throw new Error("FRONTEND_RUNTIME_INTEGRITY_VIOLATION: Missing #root");
}
const root = ReactDOM.createRoot(rootElement);

// PHASE 3: CERTIFIED ENTRY POINT
// Contracts:
// 1. Shell-First (App)
// 2. Visibility Invariant (UIContractGuard)
// 3. Layer Diagnostics (renderCheckpoint)

root.render(
  <React.StrictMode>
    <UIContractGuard>
      <App />
    </UIContractGuard>
  </React.StrictMode>,
);

// Layer 1: React Root Mount
renderCheckpoint(1, 'React Root Mount');

// Phase 6: Service Worker / Cache Neutralizer
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (let registration of registrations) {
      console.warn('[TRUTH_SWEEP] Unregistering Service Worker for Cache Neutralization:', registration.active?.scriptURL);
      registration.unregister();
    }
  });
}

// Atomic Cache Busting: Log current refresh identity
console.log(`[TRUTH_SWEEP] Session Boot: ${new Date().toISOString()}`);
